import { LlmOutputFailureTypesEnum } from '@eluve/graphql-types';

export type SummarizationErrorMessage = {
  title: string;
  description: string;
  showRegenerateHint: boolean;
  showTemplateSelector?: boolean;
  variant: 'error' | 'warning';
};

const ELUVE_SUPPORT_EMAIL = 'support@eluve.com';

const COMMON_ERROR_TITLES = {
  SOMETHING_WRONG: 'Something went wrong',
  FAILED_TO_GENERATE: 'Failed to generate note',
} as const;

const COMMON_ERROR_DESCRIPTIONS = {
  TEAM_INVESTIGATING: `Don't worry! Our team is working on fixing this issue. You can try again, or email ${ELUVE_SUPPORT_EMAIL} if the problem continues.`,
  RECORDING_TOO_SHORT:
    "This recording is too short or doesn't have enough clinical information to generate a note. Resume recording to add more content.",
  TEMPLATE_SELECTION_REQUIRED:
    'Note generation failed. Select a different template below and click Regenerate to try again.',
} as const;

export const SUMMARIZATION_ERROR_MESSAGES: Record<
  LlmOutputFailureTypesEnum,
  SummarizationErrorMessage
> = {
  TEMPLATE_MISSING: {
    variant: 'warning',
    title: COMMON_ERROR_TITLES.FAILED_TO_GENERATE,
    description: COMMON_ERROR_DESCRIPTIONS.TEMPLATE_SELECTION_REQUIRED,
    showRegenerateHint: true,
    showTemplateSelector: true,
  },
  DEGRADED_TRANSCRIPT: {
    variant: 'error',
    title: COMMON_ERROR_TITLES.SOMETHING_WRONG,
    description: COMMON_ERROR_DESCRIPTIONS.TEAM_INVESTIGATING,
    showRegenerateHint: false,
  },
  NO_MEDICAL_CONTEXT: {
    variant: 'warning',
    title: COMMON_ERROR_TITLES.FAILED_TO_GENERATE,
    description: COMMON_ERROR_DESCRIPTIONS.RECORDING_TOO_SHORT,
    showRegenerateHint: false,
  },
  TRANSCRIPT_TOO_SHORT: {
    variant: 'warning',
    title: COMMON_ERROR_TITLES.FAILED_TO_GENERATE,
    description: COMMON_ERROR_DESCRIPTIONS.RECORDING_TOO_SHORT,
    showRegenerateHint: false,
  },
  LLM_GENERATION_ERROR: {
    variant: 'error',
    title: COMMON_ERROR_TITLES.SOMETHING_WRONG,
    description: COMMON_ERROR_DESCRIPTIONS.TEAM_INVESTIGATING,
    showRegenerateHint: true,
  },
  SCHEMA_VALIDATION_ERROR: {
    variant: 'warning',
    title: COMMON_ERROR_TITLES.FAILED_TO_GENERATE,
    description: COMMON_ERROR_DESCRIPTIONS.TEMPLATE_SELECTION_REQUIRED,
    showRegenerateHint: true,
    showTemplateSelector: true,
  },
  UNKNOWN: {
    variant: 'error',
    title: COMMON_ERROR_TITLES.SOMETHING_WRONG,
    description: COMMON_ERROR_DESCRIPTIONS.TEAM_INVESTIGATING,
    showRegenerateHint: true,
  },
};

export const getSummarizationErrorMessage = (
  failureType: LlmOutputFailureTypesEnum | null | undefined,
): SummarizationErrorMessage => {
  if (!failureType) {
    return SUMMARIZATION_ERROR_MESSAGES.UNKNOWN;
  }

  return (
    SUMMARIZATION_ERROR_MESSAGES[failureType] ??
    SUMMARIZATION_ERROR_MESSAGES.UNKNOWN
  );
};
